import React, { useState } from "react";
import APIProductService from "../../services/APIProductService";
import { connect } from "react-redux";
import { getUserIdFromState } from "../../mappers/backendMappers";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { JSONTree } from "react-json-tree";
import { categoriesParser } from "../../constants/parse/product";
import SwaggerOperationRenderer from "../SwaggerOperationRenderer";
import SwaggerEntityRenderer from "../SwaggerEntityRenderer";
import "./APIProductServiceTest.css";

// JSON Tree Theme
const jsonTheme = {
  scheme: "monokai",
  author: "wimer hazenberg (http://www.monokai.nl)",
  base00: "#1e293b",
  base01: "#383830",
  base02: "#49483e",
  base03: "#75715e",
  base04: "#a59f85",
  base05: "#f8f8f2",
  base06: "#f5f4f1",
  base07: "#f9f8f5",
  base08: "#f92672",
  base09: "#fd971f",
  base0A: "#f4bf75",
  base0B: "#a6e22e",
  base0C: "#a1efe4",
  base0D: "#66d9ef",
  base0E: "#ae81ff",
  base0F: "#cc6633",
};

const APIProductServiceTest = ({ userId, csrfToken }) => {
  const [productUuid, setProductUuid] = useState("");
  const [apiName, setApiName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productResult, setProductResult] = useState(null);
  const [apiResult, setApiResult] = useState(null);
  const [swaggerResult, setSwaggerResult] = useState(null);
  const [swaggerDescription, setSwaggerDescription] = useState(null);
  const [productList, setProductList] = useState(null);
  const [categoryList, setCategoryList] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState(null);
  const [activeTab, setActiveTab] = useState("productList");
  const [productAdvantages, setProductAdvantages] = useState(null);
  const [availableApis, setAvailableApis] = useState([]);
  const [swaggerPaths, setSwaggerPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState("");
  const [extractedOperation, setExtractedOperation] = useState(null);
  const [swaggerEntities, setSwaggerEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [extractedEntity, setExtractedEntity] = useState(null);
  const [structuresViewMode, setStructuresViewMode] = useState("table");
  const [operationViewMode, setOperationViewMode] = useState("structured"); // 'structured', 'json', or 'raw'
  const [entityViewMode, setEntityViewMode] = useState("structured"); // 'structured', 'json', or 'raw'

  const handleFetchProduct = async () => {
    if (!productUuid) {
      setError("Please enter a Product UUID");
      return;
    }

    setLoading(true);
    setError(null);
    setProductResult(null);
    setApiResult(null);
    setAvailableApis([]);
    setApiName("");

    try {
      const result = await APIProductService.getProductDocument(
        productUuid,
        userId,
        csrfToken
      );
      setProductResult(result);

      // Extract available APIs from the product document
      const apiEndpoints =
        result?.original?.apiEndpoints ||
        result?.original?.ApiEndpoints ||
        result?.backend?.apiEndpoints ||
        result?.backend?.ApiEndpoints ||
        [];

      const apis = apiEndpoints
        .map((endpoint) => ({
          name: endpoint.apiName || endpoint.ApiName || "",
          description: endpoint.description || endpoint.Description || "",
          version: endpoint.apiVersion || endpoint.ApiVersion || "",
        }))
        .filter((api) => api.name); // Filter out empty names

      setAvailableApis(apis);
      if (apis.length > 0) {
        setApiName(apis[0].name); // Set first API as default
      }

      setError(null);
      setActiveTab("product"); // Switch to product tab when loaded
    } catch (err) {
      setError(err.message);
      setProductResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFindApi = () => {
    if (!apiName) {
      setError("Please enter an API name");
      return;
    }

    setError(null);
    setSwaggerResult(null); // Clear swagger when finding new API
    const api = APIProductService.findAPI(apiName);

    if (api) {
      setApiResult(api);
      setError(null);
      setActiveTab("api"); // Switch to API tab when loaded
    } else {
      setApiResult(null);
      setError("API not found or no product document loaded");
    }
  };

  const handleGetSwagger = () => {
    setError(null);
    const swagger = APIProductService.getSwagger();

    if (swagger) {
      setSwaggerResult(swagger);

      // Extract paths and operations
      const paths = [];
      if (swagger.paths) {
        Object.entries(swagger.paths).forEach(([path, pathItem]) => {
          // Get all HTTP methods for this path
          const methods = [
            "get",
            "post",
            "put",
            "delete",
            "patch",
            "options",
            "head",
          ];
          methods.forEach((method) => {
            if (pathItem[method]) {
              const operation = pathItem[method];
              paths.push({
                value: `${method.toUpperCase()} ${path}`,
                path: path,
                method: method.toUpperCase(),
                summary: operation.summary || "",
                operationId: operation.operationId || "",
                description: operation.description || "",
              });
            }
          });
        });
      }

      setSwaggerPaths(paths);
      if (paths.length > 0) {
        setSelectedPath(paths[0].value);
      }

      // Extract entities/definitions
      const entities = [];
      const definitions =
        swagger.definitions || swagger.components?.schemas || {};
      if (definitions && Object.keys(definitions).length > 0) {
        Object.entries(definitions).forEach(([name, schema]) => {
          entities.push({
            value: name,
            name: name,
            type: schema.type || "object",
            description: schema.description || "",
            required: schema.required || [],
            properties: schema.properties
              ? Object.keys(schema.properties).length
              : 0,
          });
        });
      }

      setSwaggerEntities(entities);
      if (entities.length > 0) {
        setSelectedEntity(entities[0].value);
      }

      setError(null);
      setActiveTab("swagger"); // Switch to Swagger tab when loaded
    } else {
      setSwaggerResult(null);
      setSwaggerPaths([]);
      setSwaggerEntities([]);
      setError("No Swagger data found or API not loaded");
    }
  };

  const handleGetSwaggerDescription = () => {
    setError(null);
    const description = APIProductService.getSwaggerDescription();

    if (description) {
      setSwaggerDescription(description);
      setError(null);
      setActiveTab("description"); // Switch to Description tab when loaded
    } else {
      setSwaggerDescription(null);
      setError("No Swagger description found or API not loaded");
    }
  };

  const handleDownloadSwagger = () => {
    setError(null);
    const success = APIProductService.downloadSwagger();

    if (!success) {
      setError("No Swagger data available to download");
    }
  };

  const handleFetchProductList = async () => {
    setLoading(true);
    setError(null);

    try {
      const list = await APIProductService.getProductList(userId, csrfToken);
      setProductList(list);
      setActiveTab("productList");
    } catch (error) {
      console.error("Error fetching product list:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const categories = await APIProductService.getCategoryList(
        userId,
        csrfToken
      );
      setCategoryList(categories);
      setActiveTab("categories");
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterByCategory = () => {
    if (!categoryFilter) {
      setError("Please select a category");
      return;
    }
    setError(null);
    const filtered = APIProductService.getProductsByCategory(categoryFilter);
    setFilteredProducts(filtered);
    setActiveTab("filtered");
  };

  const handleClear = () => {
    APIProductService.clear();
    setProductResult(null);
    setApiResult(null);
    setSwaggerResult(null);
    setSwaggerDescription(null);
    setProductList(null);
    setCategoryList(null);
    setCategoryFilter(null);
    setFilteredProducts(null);
    setProductAdvantages(null);
    setAvailableApis([]);
    setSwaggerPaths([]);
    setSelectedPath("");
    setExtractedOperation(null);
    setError(null);
    setProductUuid("");
    setApiName("");
  };

  const handleDownloadProduct = () => {
    if (productResult) {
      const success = APIProductService.downloadProduct(productResult);
      if (success) {
        console.log("Product document downloaded successfully");
      } else {
        setError("Failed to download product document");
      }
    }
  };

  const handleViewAdvantages = () => {
    if (productResult?.original?.productAdvantages) {
      setProductAdvantages(productResult.original.productAdvantages);
      setActiveTab("advantages");
    } else {
      setError("No advantages found in the product document");
    }
  };

  // Helper function to resolve schema references and collect all related schemas
  const collectRelatedSchemas = (
    schema,
    definitions,
    collected = new Set(),
    depth = 0
  ) => {
    if (depth > 10 || !schema) return;

    if (schema.$ref) {
      const refName = schema.$ref.split("/").pop();
      if (!collected.has(refName) && definitions[refName]) {
        collected.add(refName);
        collectRelatedSchemas(
          definitions[refName],
          definitions,
          collected,
          depth + 1
        );
      }
    }

    if (schema.type === "array" && schema.items) {
      collectRelatedSchemas(schema.items, definitions, collected, depth + 1);
    }

    if (schema.type === "object" && schema.properties) {
      Object.values(schema.properties).forEach((prop) => {
        collectRelatedSchemas(prop, definitions, collected, depth + 1);
      });
    }

    if (schema.allOf) {
      schema.allOf.forEach((subSchema) => {
        collectRelatedSchemas(subSchema, definitions, collected, depth + 1);
      });
    }

    if (schema.oneOf) {
      schema.oneOf.forEach((subSchema) => {
        collectRelatedSchemas(subSchema, definitions, collected, depth + 1);
      });
    }

    if (schema.anyOf) {
      schema.anyOf.forEach((subSchema) => {
        collectRelatedSchemas(subSchema, definitions, collected, depth + 1);
      });
    }
  };

  // Helper function to generate detailed JSON structure with comments
  const generateDetailedJsonStructure = (operation, definitions) => {
    const structures = {};
    const collectedSchemas = new Set();

    // Collect schemas from request body
    if (operation.requestBody?.content) {
      Object.values(operation.requestBody.content).forEach((mediaType) => {
        if (mediaType.schema) {
          collectRelatedSchemas(
            mediaType.schema,
            definitions,
            collectedSchemas
          );
        }
      });
    }

    // Collect schemas from parameters
    if (operation.parameters) {
      operation.parameters.forEach((param) => {
        if (param.schema) {
          collectRelatedSchemas(param.schema, definitions, collectedSchemas);
        }
      });
    }

    // Collect schemas from responses
    if (operation.responses) {
      Object.values(operation.responses).forEach((response) => {
        if (response.content) {
          Object.values(response.content).forEach((mediaType) => {
            if (mediaType.schema) {
              collectRelatedSchemas(
                mediaType.schema,
                definitions,
                collectedSchemas
              );
            }
          });
        }
      });
    }

    // Build detailed structure for each collected schema
    collectedSchemas.forEach((schemaName) => {
      const schema = definitions[schemaName];
      if (schema) {
        structures[schemaName] = buildDetailedStructure(
          schema,
          schemaName,
          definitions
        );
      }
    });

    return structures;
  };

  // Helper function to build detailed structure with field descriptions
  const buildDetailedStructure = (schema, name, definitions) => {
    const structure = {
      name: name,
      description: schema.description || "No description available",
      type: schema.type || "object",
      required: schema.required || [],
      fields: {},
    };

    if (schema.properties) {
      Object.entries(schema.properties).forEach(([fieldName, fieldSchema]) => {
        structure.fields[fieldName] = {
          type: getFieldType(fieldSchema, definitions),
          required: schema.required?.includes(fieldName) || false,
          description: fieldSchema.description || "",
          format: fieldSchema.format || "",
          example: fieldSchema.example || "",
          enum: fieldSchema.enum || null,
          pattern: fieldSchema.pattern || "",
          minLength: fieldSchema.minLength,
          maxLength: fieldSchema.maxLength,
          minimum: fieldSchema.minimum,
          maximum: fieldSchema.maximum,
        };

        // Clean up undefined values
        Object.keys(structure.fields[fieldName]).forEach((key) => {
          if (
            structure.fields[fieldName][key] === undefined ||
            structure.fields[fieldName][key] === ""
          ) {
            delete structure.fields[fieldName][key];
          }
        });
      });
    }

    return structure;
  };

  // Helper function to get field type including references
  const getFieldType = (fieldSchema, definitions) => {
    if (fieldSchema.$ref) {
      return `Reference to ${fieldSchema.$ref.split("/").pop()}`;
    }
    if (fieldSchema.type === "array") {
      if (fieldSchema.items?.$ref) {
        return `Array of ${fieldSchema.items.$ref.split("/").pop()}`;
      }
      return `Array of ${fieldSchema.items?.type || "any"}`;
    }
    if (fieldSchema.allOf) {
      return "Combined schema (allOf)";
    }
    if (fieldSchema.oneOf) {
      return "One of multiple schemas (oneOf)";
    }
    if (fieldSchema.anyOf) {
      return "Any of multiple schemas (anyOf)";
    }
    return fieldSchema.type || "any";
  };

  // Helper function to create field value with comments
  const createFieldValue = (fieldInfo, fieldName, allStructures) => {
    let value = null;
    let comment = `/* ${
      fieldInfo.description || "No description available"
    } */`;

    // Build additional metadata comments
    const metadata = [];
    metadata.push(`Type: ${fieldInfo.type}`);
    if (fieldInfo.required) metadata.push("Required");
    if (fieldInfo.format) metadata.push(`Format: ${fieldInfo.format}`);
    if (fieldInfo.enum) metadata.push(`Enum: [${fieldInfo.enum.join(", ")}]`);
    if (fieldInfo.pattern) metadata.push(`Pattern: ${fieldInfo.pattern}`);
    if (fieldInfo.minLength !== undefined)
      metadata.push(`Min Length: ${fieldInfo.minLength}`);
    if (fieldInfo.maxLength !== undefined)
      metadata.push(`Max Length: ${fieldInfo.maxLength}`);
    if (fieldInfo.minimum !== undefined)
      metadata.push(`Min: ${fieldInfo.minimum}`);
    if (fieldInfo.maximum !== undefined)
      metadata.push(`Max: ${fieldInfo.maximum}`);

    const fullComment = `/* ${
      fieldInfo.description || "No description available"
    } | ${metadata.join(" | ")} */`;

    // Determine the value based on type
    if (fieldInfo.type.startsWith("Reference to ")) {
      const refName = fieldInfo.type.replace("Reference to ", "");
      if (allStructures[refName]) {
        // Nested structure
        value = createConsolidatedStructure(
          allStructures[refName],
          allStructures,
          new Set()
        );
      } else {
        value = `<${refName}>`;
      }
    } else if (fieldInfo.type.startsWith("Array of ")) {
      const itemType = fieldInfo.type.replace("Array of ", "");
      if (allStructures[itemType]) {
        value = [
          createConsolidatedStructure(
            allStructures[itemType],
            allStructures,
            new Set()
          ),
        ];
      } else {
        value = [`<${itemType}>`];
      }
    } else {
      // Provide example value based on type
      if (fieldInfo.example !== undefined) {
        value = fieldInfo.example;
      } else if (fieldInfo.enum) {
        value = fieldInfo.enum[0];
      } else {
        switch (fieldInfo.type) {
          case "string":
            value = "<string>";
            break;
          case "number":
            value = 0;
            break;
          case "integer":
            value = 0;
            break;
          case "boolean":
            value = false;
            break;
          case "object":
            value = {};
            break;
          case "array":
            value = [];
            break;
          default:
            value = null;
        }
      }
    }

    return { value, comment: fullComment };
  };

  // Helper function to create consolidated structure recursively
  const createConsolidatedStructure = (
    structure,
    allStructures,
    visited = new Set()
  ) => {
    // Prevent infinite recursion
    if (visited.has(structure.name)) {
      return `<Circular reference to ${structure.name}>`;
    }
    visited.add(structure.name);

    const result = {};

    // Add structure metadata as comment
    result[`__${structure.name}_info__`] = `/* ${
      structure.description
    } | Required fields: ${structure.required.join(", ") || "none"} */`;

    // Add each field
    Object.entries(structure.fields).forEach(([fieldName, fieldInfo]) => {
      const { value, comment } = createFieldValue(
        fieldInfo,
        fieldName,
        allStructures
      );
      result[`__${fieldName}_comment__`] = comment;
      result[fieldName] = value;
    });

    return result;
  };

  // Helper function to create the complete consolidated JSON view
  const createConsolidatedJsonView = (structures) => {
    // Identify root structures (not referenced by others)
    const referencedStructures = new Set();
    Object.values(structures).forEach((structure) => {
      Object.values(structure.fields).forEach((field) => {
        if (field.type.includes("Reference to ")) {
          const refName = field.type
            .replace("Reference to ", "")
            .replace("Array of ", "");
          referencedStructures.add(refName);
        }
      });
    });

    const rootStructures = Object.keys(structures).filter(
      (name) => !referencedStructures.has(name)
    );

    // Create consolidated view
    const consolidated = {
      "/* API Operation Structures */":
        "/* Complete structure definitions with all nested dependencies */",
    };

    if (rootStructures.length === 1) {
      // Single root - show it directly with nested structures
      const rootName = rootStructures[0];
      const rootStructure = structures[rootName];
      Object.assign(
        consolidated,
        createConsolidatedStructure(rootStructure, structures, new Set())
      );
    } else if (rootStructures.length > 1) {
      // Multiple roots - show them side by side
      rootStructures.forEach((rootName) => {
        const rootStructure = structures[rootName];
        consolidated[rootName] = createConsolidatedStructure(
          rootStructure,
          structures,
          new Set()
        );
      });
    } else {
      // No clear roots, show all as independent
      Object.entries(structures).forEach(([name, structure]) => {
        consolidated[name] = createConsolidatedStructure(
          structure,
          structures,
          new Set()
        );
      });
    }

    return consolidated;
  };

  const handleExtractOperation = () => {
    if (!selectedPath || !swaggerResult) {
      setError("Please select a path/operation");
      return;
    }

    // Find the selected operation details
    const selectedOp = swaggerPaths.find((p) => p.value === selectedPath);
    if (!selectedOp) {
      setError("Selected operation not found");
      return;
    }

    // Extract the operation from the swagger document
    const pathItem = swaggerResult.paths[selectedOp.path];
    const operation = pathItem[selectedOp.method.toLowerCase()];

    // Get all definitions/schemas
    const definitions =
      swaggerResult.definitions || swaggerResult.components?.schemas || {};

    // Generate detailed JSON structures
    const detailedStructures = generateDetailedJsonStructure(
      operation,
      definitions
    );

    // Build the extracted operation document
    const extractedDoc = {
      path: selectedOp.path,
      method: selectedOp.method,
      summary: operation.summary || "",
      description: operation.description || "",
      operationId: operation.operationId || "",
      // Original operation details
      operation: operation,
      // Detailed JSON structures with comments
      jsonStructures: detailedStructures,
      // Include relevant global definitions if referenced
      definitions: definitions,
      securityDefinitions:
        swaggerResult.securityDefinitions ||
        swaggerResult.components?.securitySchemes ||
        {},
      // Include base information
      basePath: swaggerResult.basePath || "",
      host: swaggerResult.host || "",
      schemes: swaggerResult.schemes || [],
      servers: swaggerResult.servers || [],
    };

    setExtractedOperation(extractedDoc);
    setActiveTab("operation");
    setError(null);
  };

  const handleExtractEntity = () => {
    if (!selectedEntity || !swaggerResult) {
      setError("Please select an entity/definition");
      return;
    }

    // Get all definitions/schemas
    const definitions =
      swaggerResult.definitions || swaggerResult.components?.schemas || {};

    // Find the selected entity
    const entity = definitions[selectedEntity];
    if (!entity) {
      setError("Selected entity not found");
      return;
    }

    // Build the extracted entity document
    const extractedDoc = {
      name: selectedEntity,
      entity: entity,
      type: entity.type || "object",
      description: entity.description || "",
      required: entity.required || [],
      properties: entity.properties || {},
      // Include all definitions for reference resolution
      definitions: definitions,
      // Additional metadata
      discriminator: entity.discriminator || null,
      xml: entity.xml || null,
      example: entity.example || null,
      examples: entity.examples || null,
      // Include base information from swagger
      basePath: swaggerResult.basePath || "",
      host: swaggerResult.host || "",
      servers: swaggerResult.servers || [],
    };

    setExtractedEntity(extractedDoc);
    setActiveTab("entity");
    setError(null);
  };

  const handleDownloadAll = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create a progress tracking state
      const progressCallback = (progress) => {
        console.log(
          `Download progress: ${progress.current}/${progress.total} - ${
            progress.productName || ""
          }`
        );
        if (progress.status === "completed") {
          console.log(
            `Downloaded: ${progress.downloaded}, Failed: ${progress.failed}`
          );
        }
      };

      const summary = await APIProductService.downloadAllAPIProducts(
        userId,
        csrfToken,
        progressCallback
      );

      // Show summary
      if (summary.success) {
        alert(`Successfully downloaded all ${summary.total} API products!`);
      } else {
        alert(
          `Downloaded ${summary.downloaded.length} products. Failed: ${summary.failed.length}`
        );
        console.error("Failed products:", summary.failed);
      }
    } catch (error) {
      console.error("Error downloading all products:", error);
      setError(`Failed to download all products: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="test-page-wrapper">
        <div className="vertical-border-left"></div>
        <div className="vertical-border-right"></div>
        <div className="api-product-service-test">
          <h1>API Product Service Test</h1>

          <div className="test-section">
            <h2>Load API Products & Categories</h2>
            <div className="input-group">
              <button
                onClick={handleFetchProductList}
                disabled={loading}
                className="primary-button"
              >
                {loading ? "Loading..." : "Fetch All Products"}
              </button>
              <button
                onClick={handleFetchCategories}
                disabled={loading}
                className="primary-button"
              >
                {loading ? "Loading..." : "Fetch Categories"}
              </button>
              <button
                onClick={handleDownloadAll}
                disabled={loading}
                className="download-all-button"
              >
                {loading ? "Downloading..." : "📥 Download All Products"}
              </button>
            </div>

            <h2>Load Specific Product</h2>
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter Product UUID"
                value={productUuid}
                onChange={(e) => setProductUuid(e.target.value)}
                disabled={loading}
              />
              <button onClick={handleFetchProduct} disabled={loading}>
                {loading ? "Loading..." : "Fetch Product"}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <h3>Error:</h3>
              <p>{error}</p>
            </div>
          )}

          {/* Category Filter Section */}
          {categoryList && productList && (
            <div className="test-section">
              <h3>Filter Products by Category</h3>
              <div className="input-group">
                <select
                  value={categoryFilter?.value || ""}
                  onChange={(e) => {
                    const selected = categoryList.find(
                      (cat) => (cat.id || cat.Id) === e.target.value
                    );
                    setCategoryFilter(
                      selected
                        ? {
                            value: selected.id || selected.Id,
                            name: selected.name || selected.Name,
                          }
                        : null
                    );
                  }}
                >
                  <option value="">Select a category...</option>
                  {categoryList.map((cat) => (
                    <option key={cat.id || cat.Id} value={cat.id || cat.Id}>
                      {cat.name || cat.Name}
                    </option>
                  ))}
                </select>
                <button onClick={handleFilterByCategory}>
                  Filter Products
                </button>
              </div>
            </div>
          )}

          {/* Tabs Navigation */}
          {(productList ||
            categoryList ||
            filteredProducts ||
            productResult ||
            apiResult ||
            swaggerResult ||
            swaggerDescription) && (
            <div className="tabs-container">
              <div className="tabs-header">
                {productList && (
                  <button
                    className={`tab-button ${
                      activeTab === "productList" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("productList")}
                  >
                    Product List
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {categoryList && (
                  <button
                    className={`tab-button ${
                      activeTab === "categories" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("categories")}
                  >
                    Categories
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {filteredProducts && (
                  <button
                    className={`tab-button ${
                      activeTab === "filtered" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("filtered")}
                  >
                    Filtered Products
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {productResult && (
                  <button
                    className={`tab-button ${
                      activeTab === "product" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("product")}
                  >
                    Product Document
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {apiResult && (
                  <button
                    className={`tab-button ${
                      activeTab === "api" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("api")}
                  >
                    API Document
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {swaggerResult && (
                  <button
                    className={`tab-button ${
                      activeTab === "swagger" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("swagger")}
                  >
                    Swagger Specification
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {swaggerDescription && (
                  <button
                    className={`tab-button ${
                      activeTab === "description" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("description")}
                  >
                    Swagger Description
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {productAdvantages && (
                  <button
                    className={`tab-button ${
                      activeTab === "advantages" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("advantages")}
                  >
                    Product Advantages
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {extractedOperation && (
                  <button
                    className={`tab-button ${
                      activeTab === "operation" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("operation")}
                  >
                    Operation
                    <span className="tab-indicator"></span>
                  </button>
                )}
                {extractedEntity && (
                  <button
                    className={`tab-button ${
                      activeTab === "entity" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("entity")}
                  >
                    Entity
                    <span className="tab-indicator"></span>
                  </button>
                )}
              </div>

              <div className="tab-content">
                {/* Product List Tab */}
                {activeTab === "productList" && productList && (
                  <div className="tab-panel">
                    <div className="result-content json-tree-container">
                      <h3>
                        API Products List (
                        {Array.isArray(productList)
                          ? productList.length
                          : Array.isArray(productList?.data)
                          ? productList.data.length
                          : Array.isArray(productList?.Data)
                          ? productList.Data.length
                          : 0}{" "}
                        products):
                      </h3>
                      <JSONTree
                        data={productList}
                        theme={jsonTheme}
                        invertTheme={false}
                        hideRoot={true}
                        shouldExpandNode={(keyPath) => keyPath.length <= 1}
                      />
                    </div>
                  </div>
                )}

                {/* Categories Tab */}
                {activeTab === "categories" && categoryList && (
                  <div className="tab-panel">
                    <div className="result-content json-tree-container">
                      <h3>
                        Product Categories ({categoryList.length} categories):
                      </h3>
                      <JSONTree
                        data={categoryList}
                        theme={jsonTheme}
                        invertTheme={false}
                        hideRoot={true}
                        shouldExpandNode={(keyPath) => keyPath.length <= 2}
                      />
                    </div>
                  </div>
                )}

                {/* Filtered Products Tab */}
                {activeTab === "filtered" && filteredProducts && (
                  <div className="tab-panel">
                    <div className="result-content json-tree-container">
                      <h3>
                        Filtered Products ({filteredProducts.length} products) -
                        Category: {categoryFilter?.name}:
                      </h3>
                      <JSONTree
                        data={filteredProducts}
                        theme={jsonTheme}
                        invertTheme={false}
                        hideRoot={true}
                        shouldExpandNode={(keyPath) => keyPath.length <= 2}
                      />
                    </div>
                  </div>
                )}

                {/* Product Document Tab */}
                {activeTab === "product" && productResult && (
                  <div className="tab-panel">
                    <div className="tab-header-actions">
                      <button
                        onClick={handleDownloadProduct}
                        className="download-button"
                      >
                        📥 Download Product Document
                      </button>
                      {productResult?.original?.productAdvantages && (
                        <button
                          onClick={handleViewAdvantages}
                          className="action-button"
                        >
                          📖 View Advantages
                        </button>
                      )}
                    </div>
                    <div className="result-content json-tree-container">
                      <h3>Product Document:</h3>
                      <JSONTree
                        data={productResult.original}
                        theme={jsonTheme}
                        invertTheme={false}
                        hideRoot={true}
                        shouldExpandNode={(keyPath) => keyPath.length <= 2}
                      />
                    </div>

                    {/* Find API Section */}
                    <div className="tab-action-section">
                      <h3>Find API Endpoint</h3>
                      <div className="input-group">
                        <select
                          value={apiName}
                          onChange={(e) => setApiName(e.target.value)}
                          className="api-dropdown"
                          disabled={availableApis.length === 0}
                        >
                          {availableApis.length === 0 ? (
                            <option value="">No APIs available</option>
                          ) : (
                            <>
                              <option value="">Select an API</option>
                              {availableApis.map((api, index) => (
                                <option key={index} value={api.name}>
                                  {api.name}
                                  {api.version && ` (v${api.version})`}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                        <button
                          onClick={handleFindApi}
                          disabled={!apiName || availableApis.length === 0}
                        >
                          Find API
                        </button>
                      </div>
                      {availableApis.length > 0 && apiName && (
                        <div className="api-info">
                          {availableApis.find((api) => api.name === apiName)
                            ?.description && (
                            <p className="api-description">
                              {
                                availableApis.find(
                                  (api) => api.name === apiName
                                )?.description
                              }
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* API Document Tab */}
                {activeTab === "api" && apiResult && (
                  <div className="tab-panel">
                    <div className="result-content json-tree-container">
                      <JSONTree
                        data={apiResult}
                        theme={jsonTheme}
                        invertTheme={false}
                        hideRoot={true}
                        shouldExpandNode={() => true}
                      />
                    </div>

                    {/* Get Swagger Section */}
                    <div className="tab-action-section">
                      <h3>Get Swagger Specification</h3>
                      <button
                        onClick={handleGetSwagger}
                        className="action-button"
                      >
                        Get Swagger
                      </button>
                    </div>
                  </div>
                )}

                {/* Swagger Specification Tab */}
                {activeTab === "swagger" && swaggerResult && (
                  <div className="tab-panel">
                    <div className="result-content json-tree-container">
                      <h3>Full Swagger Specification:</h3>
                      <JSONTree
                        data={swaggerResult}
                        theme={jsonTheme}
                        invertTheme={false}
                        hideRoot={true}
                        shouldExpandNode={(keyPath) => keyPath.length <= 2}
                      />
                    </div>

                    {/* Path/Operation Selection Section */}
                    {swaggerPaths.length > 0 && (
                      <div className="tab-action-section">
                        <h3>Extract Operation</h3>
                        <div className="input-group">
                          <select
                            value={selectedPath}
                            onChange={(e) => setSelectedPath(e.target.value)}
                            className="api-dropdown"
                          >
                            <option value="">Select an operation</option>
                            {swaggerPaths.map((pathOp, index) => (
                              <option key={index} value={pathOp.value}>
                                {pathOp.value}
                                {pathOp.summary && ` - ${pathOp.summary}`}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={handleExtractOperation}
                            disabled={!selectedPath}
                            className="action-button"
                          >
                            Extract Operation
                          </button>
                        </div>
                        {selectedPath && (
                          <div className="api-info">
                            {swaggerPaths.find((p) => p.value === selectedPath)
                              ?.description && (
                              <p className="api-description">
                                {
                                  swaggerPaths.find(
                                    (p) => p.value === selectedPath
                                  )?.description
                                }
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Entity/Definition Selection Section */}
                    {swaggerEntities.length > 0 && (
                      <div className="tab-action-section">
                        <h3>Extract Entity</h3>
                        <div className="input-group">
                          <select
                            value={selectedEntity}
                            onChange={(e) => setSelectedEntity(e.target.value)}
                            className="api-dropdown"
                          >
                            <option value="">
                              Select an entity/definition
                            </option>
                            {swaggerEntities.map((entity, index) => (
                              <option key={index} value={entity.value}>
                                {entity.name}
                                {entity.type &&
                                  entity.type !== "object" &&
                                  ` (${entity.type})`}
                                {entity.properties > 0 &&
                                  ` - ${entity.properties} properties`}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={handleExtractEntity}
                            disabled={!selectedEntity}
                            className="action-button"
                          >
                            Extract Entity
                          </button>
                        </div>
                        {selectedEntity && (
                          <div className="selection-details">
                            <p>
                              <strong>Selected Entity:</strong> {selectedEntity}
                            </p>
                            {swaggerEntities.find(
                              (e) => e.value === selectedEntity
                            )?.description && (
                              <p>
                                <strong>Description:</strong>{" "}
                                {
                                  swaggerEntities.find(
                                    (e) => e.value === selectedEntity
                                  ).description
                                }
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Swagger Actions Section */}
                    <div className="tab-action-section">
                      <h3>Swagger Actions</h3>
                      <div className="button-group">
                        <button
                          onClick={handleGetSwaggerDescription}
                          className="action-button"
                        >
                          Get Swagger Description
                        </button>
                        <button
                          onClick={handleDownloadSwagger}
                          className="action-button download-button"
                        >
                          📥 Download Swagger File
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Swagger Description Tab */}
                {activeTab === "description" && swaggerDescription && (
                  <div className="tab-panel">
                    <div className="result-content markdown-content">
                      <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                        {swaggerDescription}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Product Advantages Tab */}
                {activeTab === "advantages" && productAdvantages && (
                  <div className="tab-panel">
                    <div className="result-content advantages-content">
                      <h2>Product Advantages</h2>
                      {productAdvantages.map((advantage, index) => (
                        <div key={index} className="advantage-item">
                          <h3>{advantage.title}</h3>
                          <div className="markdown-content">
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                              {advantage.description}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Operation Tab */}
                {activeTab === "operation" && extractedOperation && (
                  <div className="tab-panel">
                    <div className="result-content">
                      <div className="operation-header-section">
                        <h3>
                          Extracted Operation: {extractedOperation.method}{" "}
                          {extractedOperation.path}
                        </h3>
                        <div className="operation-view-toggle">
                          <button
                            className={`view-mode-btn ${
                              operationViewMode === "structured" ? "active" : ""
                            }`}
                            onClick={() => setOperationViewMode("structured")}
                          >
                            Structured View
                          </button>
                          <button
                            className={`view-mode-btn ${
                              operationViewMode === "json" ? "active" : ""
                            }`}
                            onClick={() => setOperationViewMode("json")}
                          >
                            JSON View
                          </button>
                          <button
                            className={`view-mode-btn ${
                              operationViewMode === "raw" ? "active" : ""
                            }`}
                            onClick={() => setOperationViewMode("raw")}
                          >
                            Raw View
                          </button>
                        </div>
                      </div>

                      {/* Structured View */}
                      {operationViewMode === "structured" && (
                        <div className="structured-operation-view">
                          <SwaggerOperationRenderer
                            operation={extractedOperation.operation}
                            definitions={extractedOperation.definitions}
                            path={extractedOperation.path}
                            method={extractedOperation.method}
                            showSummary={true}
                          />
                        </div>
                      )}

                      {/* JSON View */}
                      {operationViewMode === "json" &&
                        extractedOperation.jsonStructures &&
                        Object.keys(extractedOperation.jsonStructures).length >
                          0 && (
                          <div className="json-structures-section">
                            <div className="structures-header">
                              <h4>JSON Structures Used by This Operation</h4>
                              <button
                                className="structures-view-toggle"
                                onClick={() =>
                                  setStructuresViewMode(
                                    structuresViewMode === "table"
                                      ? "json"
                                      : "table"
                                  )
                                }
                                title={`Switch to ${
                                  structuresViewMode === "table"
                                    ? "JSON"
                                    : "Table"
                                } view`}
                              >
                                <span className="toggle-icon">
                                  {structuresViewMode === "table" ? "{ }" : "☰"}
                                </span>
                                <span className="toggle-label">
                                  {structuresViewMode === "table"
                                    ? "JSON View"
                                    : "Table View"}
                                </span>
                              </button>
                            </div>

                            {structuresViewMode === "table" ? (
                              /* Table View */
                              Object.entries(
                                extractedOperation.jsonStructures
                              ).map(([structName, structure]) => (
                                <div
                                  key={structName}
                                  className="json-structure-item"
                                >
                                  <h5 className="structure-name">
                                    {structName}
                                  </h5>
                                  <p className="structure-description">
                                    <em>{structure.description}</em>
                                  </p>
                                  {structure.required &&
                                    structure.required.length > 0 && (
                                      <p className="structure-required">
                                        <strong>Required fields:</strong>{" "}
                                        {structure.required.join(", ")}
                                      </p>
                                    )}
                                  <div className="structure-fields">
                                    <table className="structure-fields-table">
                                      <thead>
                                        <tr>
                                          <th>Field Name</th>
                                          <th>Type</th>
                                          <th>Required</th>
                                          <th>Description</th>
                                          <th>Additional Info</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {Object.entries(structure.fields).map(
                                          ([fieldName, fieldInfo]) => (
                                            <tr key={fieldName}>
                                              <td className="field-name">
                                                {fieldName}
                                              </td>
                                              <td className="field-type">
                                                {fieldInfo.type}
                                              </td>
                                              <td className="field-required">
                                                {fieldInfo.required ? "✓" : "-"}
                                              </td>
                                              <td className="field-description">
                                                {fieldInfo.description || "-"}
                                              </td>
                                              <td className="field-additional">
                                                {fieldInfo.format && (
                                                  <span>
                                                    Format: {fieldInfo.format}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.example && (
                                                  <span>
                                                    Example:{" "}
                                                    {JSON.stringify(
                                                      fieldInfo.example
                                                    )}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.enum && (
                                                  <span>
                                                    Enum:{" "}
                                                    {fieldInfo.enum.join(", ")}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.pattern && (
                                                  <span>
                                                    Pattern: {fieldInfo.pattern}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.minLength && (
                                                  <span>
                                                    Min Length:{" "}
                                                    {fieldInfo.minLength}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.maxLength && (
                                                  <span>
                                                    Max Length:{" "}
                                                    {fieldInfo.maxLength}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.minimum !==
                                                  undefined && (
                                                  <span>
                                                    Min: {fieldInfo.minimum}
                                                    <br />
                                                  </span>
                                                )}
                                                {fieldInfo.maximum !==
                                                  undefined && (
                                                  <span>
                                                    Max: {fieldInfo.maximum}
                                                    <br />
                                                  </span>
                                                )}
                                                {!fieldInfo.format &&
                                                  !fieldInfo.example &&
                                                  !fieldInfo.enum &&
                                                  !fieldInfo.pattern &&
                                                  !fieldInfo.minLength &&
                                                  !fieldInfo.maxLength &&
                                                  fieldInfo.minimum ===
                                                    undefined &&
                                                  fieldInfo.maximum ===
                                                    undefined &&
                                                  "-"}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))
                            ) : (
                              /* JSON View - Consolidated */
                              <div className="json-structure-consolidated">
                                <div className="structure-json-view">
                                  <div className="json-tree-container">
                                    <JSONTree
                                      data={createConsolidatedJsonView(
                                        extractedOperation.jsonStructures
                                      )}
                                      theme={jsonTheme}
                                      invertTheme={false}
                                      hideRoot={true}
                                      shouldExpandNode={() => true}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                      {/* Raw View - Original Operation Details */}
                      {operationViewMode === "raw" && (
                        <div className="operation-raw-section">
                          <h4>Complete Operation Details</h4>
                          <div className="json-tree-container">
                            <JSONTree
                              data={extractedOperation}
                              theme={jsonTheme}
                              invertTheme={false}
                              hideRoot={true}
                              shouldExpandNode={(keyPath) =>
                                keyPath.length <= 2
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Entity Tab */}
                {activeTab === "entity" && extractedEntity && (
                  <div className="tab-panel">
                    <div className="result-content">
                      <div className="entity-header-section">
                        <h3>Extracted Entity: {extractedEntity.name}</h3>
                        <div className="entity-view-toggle">
                          <button
                            className={`view-toggle-button ${
                              entityViewMode === "structured" ? "active" : ""
                            }`}
                            onClick={() => setEntityViewMode("structured")}
                          >
                            Structured View
                          </button>
                          <button
                            className={`view-toggle-button ${
                              entityViewMode === "json" ? "active" : ""
                            }`}
                            onClick={() => setEntityViewMode("json")}
                          >
                            JSON View
                          </button>
                          <button
                            className={`view-toggle-button ${
                              entityViewMode === "raw" ? "active" : ""
                            }`}
                            onClick={() => setEntityViewMode("raw")}
                          >
                            Raw View
                          </button>
                        </div>
                      </div>

                      {/* Structured View using SwaggerEntityRenderer */}
                      {entityViewMode === "structured" && (
                        <div className="structured-entity-view">
                          <SwaggerEntityRenderer
                            entity={extractedEntity.entity}
                            entityName={extractedEntity.name}
                            definitions={extractedEntity.definitions}
                            showDescription={true}
                            showTitle={false}
                          />
                        </div>
                      )}

                      {/* JSON View - Pretty printed entity structure */}
                      {entityViewMode === "json" && (
                        <div className="entity-json-section">
                          <h4>Entity Structure</h4>
                          <div className="json-tree-container">
                            <JSONTree
                              data={extractedEntity.entity}
                              theme={jsonTheme}
                              invertTheme={false}
                              hideRoot={true}
                              shouldExpandNode={(keyPath) =>
                                keyPath.length <= 3
                              }
                            />
                          </div>

                          {/* Show examples if available */}
                          {(extractedEntity.example ||
                            extractedEntity.examples) && (
                            <div className="entity-examples-section">
                              <h4>Examples</h4>
                              <div className="json-tree-container">
                                <JSONTree
                                  data={{
                                    example: extractedEntity.example,
                                    examples: extractedEntity.examples,
                                  }}
                                  theme={jsonTheme}
                                  invertTheme={false}
                                  hideRoot={true}
                                  shouldExpandNode={() => true}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Raw View - Complete extracted entity data */}
                      {entityViewMode === "raw" && (
                        <div className="entity-raw-section">
                          <h4>Complete Entity Details</h4>
                          <div className="json-tree-container">
                            <JSONTree
                              data={extractedEntity}
                              theme={jsonTheme}
                              invertTheme={false}
                              hideRoot={true}
                              shouldExpandNode={(keyPath) =>
                                keyPath.length <= 2
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="test-section">
            <button onClick={handleClear} className="clear-button">
              Clear Service State
            </button>
          </div>
        </div>

        {/* Custom Service State Footer */}
        <div className="service-state-footer">
          <div className="service-state-container">
            <h3>Current Service State</h3>
            <div className="service-state-grid">
              <div className="state-item">
                <span className="state-label">Product List:</span>
                <span
                  className={`state-value ${
                    productList ? "loaded" : "not-loaded"
                  }`}
                >
                  {productList
                    ? `Loaded (${
                        Array.isArray(productList) ? productList.length : 0
                      } products)`
                    : "Not loaded"}
                </span>
              </div>
              <div className="state-item">
                <span className="state-label">Category List:</span>
                <span
                  className={`state-value ${
                    categoryList ? "loaded" : "not-loaded"
                  }`}
                >
                  {categoryList
                    ? `Loaded (${categoryList.length} categories)`
                    : "Not loaded"}
                </span>
              </div>
              <div className="state-item">
                <span className="state-label">Product Document:</span>
                <span
                  className={`state-value ${
                    APIProductService.getStoredProductDocument()
                      ? "loaded"
                      : "not-loaded"
                  }`}
                >
                  {APIProductService.getStoredProductDocument()
                    ? "Loaded"
                    : "Not loaded"}
                </span>
              </div>
              <div className="state-item">
                <span className="state-label">API Document:</span>
                <span
                  className={`state-value ${
                    APIProductService.getApiDocument() ? "loaded" : "not-loaded"
                  }`}
                >
                  {APIProductService.getApiDocument() ? "Loaded" : "Not loaded"}
                </span>
              </div>
              <div className="state-item">
                <span className="state-label">Swagger Data:</span>
                <span
                  className={`state-value ${
                    swaggerResult ? "loaded" : "not-loaded"
                  }`}
                >
                  {swaggerResult ? "Decoded" : "Not available"}
                </span>
              </div>
              <div className="state-item">
                <span className="state-label">Swagger Description:</span>
                <span
                  className={`state-value ${
                    swaggerDescription ? "loaded" : "not-loaded"
                  }`}
                >
                  {swaggerDescription ? "Filtered" : "Not available"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  userId: getUserIdFromState(state),
  csrfToken: state.applicationUser?.token,
});

export default connect(mapStateToProps)(APIProductServiceTest);
